import React, { useState, useEffect } from 'react';
import {
  Card,
  Typography,
  Row,
  Col,
  Form,
  Input,
  Button,
  Upload,
  Avatar,
  Space,
  Spin,
  Modal,
  Select,
  Switch,
  Popconfirm,
  Empty,
} from 'antd';
import {
  UserOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  CameraOutlined,
  LockOutlined,
  EnvironmentOutlined,
  PlusOutlined,
  DeleteOutlined,
  CheckOutlined,
  HomeOutlined,
  GlobalOutlined,
} from '@ant-design/icons';
import type { UploadProps } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  profileService,
  type UserProfile,
  type Address,
  type UpdateAddressData,
} from '../api/services/profileService';
import { showToast } from '../utils/toast';

const { Title, Text } = Typography;
const { TextArea } = Input;

const Profile: React.FC = () => {
  const { t } = useTranslation();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isPasswordModalVisible, setIsPasswordModalVisible] = useState(false);
  const [isAddressModalVisible, setIsAddressModalVisible] = useState(false);
  const [editingAddress, setEditingAddress] = useState<Address | null>(null);

  const [profileForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [addressForm] = Form.useForm();

  useEffect(() => {
    loadProfile();
    loadAddresses();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const data = await profileService.getProfile();
      setProfile(data);
      profileForm.setFieldsValue({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: (data.phone || '').replace(/^\+355/, ''),
        locale: data.locale || 'sq',
        bio: data.bio || '',
      });
    } catch (error: any) {
      console.error('Error loading profile:', error);
      showToast.error(error?.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const loadAddresses = async () => {
    try {
      const data = await profileService.getAddresses();
      setAddresses(data);
    } catch (error: any) {
      console.error('Error loading addresses:', error);
      showToast.error(error?.message || 'Failed to load addresses');
    }
  };

  const handleSaveProfile = async (values: any) => {
    try {
      setSaving(true);
      const updated = await profileService.updateProfile({
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phone: values.phone,
        locale: values.locale,
        bio: values.bio,
      });
      setProfile(updated);
      setIsEditing(false);
      showToast.success(t('profile.updated_success'));
    } catch (error: any) {
      console.error('Error updating profile:', error);
      showToast.error(error?.message || t('profile.update_failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarUpload: UploadProps['customRequest'] = async ({
    file,
    onSuccess,
    onError,
  }) => {
    try {
      const updated = await profileService.uploadAvatar(file as File);
      setProfile(updated);
      showToast.success(t('profile.avatar_success'));
      onSuccess?.(updated);
    } catch (error: any) {
      console.error('Error uploading avatar:', error);
      showToast.error(error?.message || t('profile.avatar_failed'));
      onError?.(error);
    }
  };

  const handlePasswordChange = async (values: {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
  }) => {
    if (values.newPassword !== values.confirmPassword) {
      showToast.error(t('profile.passwords_no_match'));
      return;
    }

    try {
      setSaving(true);
      await profileService.updatePassword(
        values.currentPassword,
        values.newPassword
      );
      passwordForm.resetFields();
      setIsPasswordModalVisible(false);
      showToast.success(t('profile.password_success'));
    } catch (error: any) {
      console.error('Error updating password:', error);
      showToast.error(error?.message || t('profile.password_failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleAddAddress = async (values: UpdateAddressData) => {
    try {
      setSaving(true);
      await profileService.addAddress(values);
      await loadAddresses();
      addressForm.resetFields();
      setIsAddressModalVisible(false);
      showToast.success(t('profile.address_added'));
    } catch (error: any) {
      console.error('Error adding address:', error);
      showToast.error(error?.message || t('profile.address_add_failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateAddress = async (values: UpdateAddressData) => {
    if (!editingAddress) return;

    try {
      setSaving(true);
      await profileService.updateAddress(editingAddress.id, values);
      await loadAddresses();
      setEditingAddress(null);
      addressForm.resetFields();
      setIsAddressModalVisible(false);
      showToast.success(t('profile.address_updated'));
    } catch (error: any) {
      console.error('Error updating address:', error);
      showToast.error(error?.message || t('profile.address_update_failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAddress = async (addressId: string) => {
    try {
      await profileService.deleteAddress(addressId);
      await loadAddresses();
      showToast.success(t('profile.address_deleted'));
    } catch (error: any) {
      console.error('Error deleting address:', error);
      showToast.error(error?.message || t('profile.address_delete_failed'));
    }
  };

  const openAddressModal = (address?: Address) => {
    if (address) {
      setEditingAddress(address);
      addressForm.setFieldsValue({
        street: address.street || '',
        city: address.city || '',
        postalCode: address.postalCode || '',
        country: address.country || '',
        isPrimary: address.isPrimary,
      });
    } else {
      setEditingAddress(null);
      addressForm.resetFields();
    }
    setIsAddressModalVisible(true);
  };

  const closeAddressModal = () => {
    setIsAddressModalVisible(false);
    setEditingAddress(null);
    addressForm.resetFields();
  };

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Empty description={t('profile.not_found')} />
      </div>
    );
  }

  return (
    <>
      <div style={{ maxWidth: '1200px', margin: '0 auto', paddingBottom: 40 }}>
        <Row gutter={[24, 24]}>
          {/* Profile Header Card */}
          <Col xs={24} lg={8}>
            <Card
              className="dash-panel shadow-sm border-0"
              style={{ textAlign: 'center', height: '100%' }}
              bodyStyle={{ padding: '40px 24px' }}
            >
              <Space
                direction="vertical"
                size="large"
                style={{ width: '100%' }}
              >
                <Upload
                  customRequest={handleAvatarUpload}
                  showUploadList={false}
                  accept="image/*"
                >
                  <div
                    style={{ position: 'relative', display: 'inline-block' }}
                  >
                    <Avatar
                      size={120}
                      src={profile.avatar}
                      icon={<UserOutlined />}
                      style={{ cursor: 'pointer', border: '4px solid #f0f0f0' }}
                    />
                    <div
                      style={{
                        position: 'absolute',
                        bottom: 0,
                        right: 0,
                        background: 'var(--color1)',
                        borderRadius: '50%',
                        padding: '8px',
                        cursor: 'pointer',
                        border: '2px solid white',
                      }}
                    >
                      <CameraOutlined
                        style={{ color: 'white', fontSize: '16px' }}
                      />
                    </div>
                  </div>
                </Upload>
                <div>
                  <Title level={3} style={{ margin: 0 }}>
                    {profile.firstName} {profile.lastName}
                  </Title>
                  <Text type="secondary">{profile.email}</Text>
                  <br />
                  <Text
                    type="secondary"
                    style={{ textTransform: 'capitalize' }}
                  >
                    {profile.role?.toLowerCase() || 'User'}
                  </Text>
                </div>
                <Button
                  type="primary"
                  icon={<LockOutlined />}
                  block
                  onClick={() => setIsPasswordModalVisible(true)}
                >
                  {t('profile.change_password')}
                </Button>
              </Space>
            </Card>
          </Col>

          {/* Profile Information Card */}
          <Col xs={24} lg={16}>
            <Card
              className="dash-panel shadow-sm border-0 mb-4"
              title={
                <Space>
                  <UserOutlined />
                  <span className="fw-bold">{t('profile.info_title')}</span>
                </Space>
              }
              bodyStyle={{ padding: '32px' }}
              extra={
                !isEditing ? (
                  <Button
                    icon={<EditOutlined />}
                    onClick={() => setIsEditing(true)}
                  >
                    {t('profile.edit')}
                  </Button>
                ) : (
                  <Space>
                    <Button
                      icon={<CloseOutlined />}
                      onClick={() => {
                        setIsEditing(false);
                        profileForm.resetFields();
                      }}
                    >
                      {t('profile.cancel')}
                    </Button>
                    <Button
                      type="primary"
                      icon={<SaveOutlined />}
                      loading={saving}
                      onClick={() => profileForm.submit()}
                    >
                      {t('profile.save')}
                    </Button>
                  </Space>
                )
              }
            >
              <Form
                form={profileForm}
                layout="vertical"
                onFinish={handleSaveProfile}
                disabled={!isEditing}
              >
                <Row gutter={16}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="firstName"
                      label={t('profile.first_name')}
                      rules={[
                        { required: true, message: t('profile.first_name_required') },
                      ]}
                    >
                      <Input size="large" placeholder={t('profile.first_name')} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="lastName"
                      label={t('profile.last_name')}
                      rules={[
                        { required: true, message: t('profile.last_name_required') },
                      ]}
                    >
                      <Input size="large" placeholder={t('profile.last_name')} />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="email"
                      label={t('profile.email')}
                      rules={[
                        { required: true, message: t('profile.email_required') },
                        {
                          type: 'email',
                          message: t('profile.email_invalid'),
                        },
                      ]}
                    >
                      <Input size="large" placeholder={t('profile.email')} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item name="phone" label={t('profile.phone')}>
                      <Input size="large" addonBefore="+355" placeholder={t('profile.phone')} />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col xs={24} sm={12}>
                    <Form.Item name="locale" label={t('profile.locale')}>
                      <Select size="large" placeholder={t('profile.locale_placeholder')}>
                        <Select.Option value="sq">Albanian (sq)</Select.Option>
                        <Select.Option value="en">English (en)</Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item name="bio" label={t('profile.bio')}>
                  <TextArea
                    rows={4}
                    placeholder={t('profile.bio_placeholder')}
                    maxLength={500}
                    showCount
                  />
                </Form.Item>
              </Form>
            </Card>
          </Col>


        </Row>

        {/* Password Change Modal */}
        <Modal
          title={t('profile.change_password')}
          open={isPasswordModalVisible}
          onCancel={() => {
            setIsPasswordModalVisible(false);
            passwordForm.resetFields();
          }}
          footer={null}
        >
          <Form
            form={passwordForm}
            layout="vertical"
            onFinish={handlePasswordChange}
          >
            <Form.Item
              name="currentPassword"
              label={t('profile.current_password')}
              rules={[
                { required: true, message: t('profile.current_password_required') },
              ]}
            >
              <Input.Password size="large" placeholder={t('profile.current_password')} />
            </Form.Item>
            <Form.Item
              name="newPassword"
              label={t('profile.new_password')}
              rules={[
                { required: true, message: t('profile.new_password_required') },
                { min: 6, message: t('profile.password_min_length') },
              ]}
            >
              <Input.Password size="large" placeholder={t('profile.new_password')} />
            </Form.Item>
            <Form.Item
              name="confirmPassword"
              label={t('profile.confirm_password')}
              dependencies={['newPassword']}
              rules={[
                { required: true, message: t('profile.confirm_password_required') },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('newPassword') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error(t('profile.passwords_no_match')));
                  },
                }),
              ]}
            >
              <Input.Password size="large" placeholder={t('profile.confirm_password')} />
            </Form.Item>
            <Form.Item>
              <Space>
                <Button
                  onClick={() => {
                    setIsPasswordModalVisible(false);
                    passwordForm.resetFields();
                  }}
                >
                  {t('profile.cancel')}
                </Button>
                <Button type="primary" htmlType="submit" loading={saving}>
                  {t('profile.update_password')}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* Address Modal */}
        <Modal
          title={editingAddress ? t('profile.edit_address') : t('profile.add_address')}
          open={isAddressModalVisible}
          onCancel={closeAddressModal}
          footer={null}
        >
          <Form
            form={addressForm}
            layout="vertical"
            onFinish={editingAddress ? handleUpdateAddress : handleAddAddress}
          >
            <Form.Item name="street" label={t('profile.street')}>
              <Input size="large" placeholder={t('profile.street_placeholder')} />
            </Form.Item>
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item name="city" label={t('profile.city')}>
                  <Input size="large" placeholder={t('profile.city')} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="postalCode" label={t('profile.postal_code')}>
                  <Input size="large" placeholder={t('profile.postal_code')} />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="country" label={t('profile.country')}>
              <Input size="large" placeholder={t('profile.country')} />
            </Form.Item>
            <Form.Item name="isPrimary" valuePropName="checked">
              <Switch
                checkedChildren={t('profile.primary_address')}
                unCheckedChildren={t('profile.regular_address')}
              />
            </Form.Item>
            <Form.Item>
              <Space>
                <Button onClick={closeAddressModal}>{t('profile.cancel')}</Button>
                <Button type="primary" htmlType="submit" loading={saving}>
                  {editingAddress ? t('profile.update_address') : t('profile.add_address')}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </>
  );
};

export default Profile;
